import React from "react";
import { X, Plus, Edit2, Check } from "lucide-react";

const CategoryManagementModal = ({
  showAddCategory,
  setShowAddCategory,
  newCategory,
  setNewCategory,
  handleAddCategory,
  allCategories,
  editingCategory,
  setEditingCategory,
  handleUpdateCategory,
  handleDeleteCategory,
  categoryStats,
  DEFAULT_CATEGORIES,
}) => {
  if (!showAddCategory) return null;
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={() => setShowAddCategory(false)}
        ></div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3
                className="text-xl font-semibold text-gray-900"
                id="modal-title"
              >
                Manage Categories
              </h3>
              <button
                onClick={() => setShowAddCategory(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Add New Category */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-3">
                Add New Category
              </h4>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) =>
                    setNewCategory({ ...newCategory, name: e.target.value })
                  }
                  placeholder="Category name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === "Enter" && handleAddCategory()}
                />
                <input
                  type="color"
                  value={newCategory.color}
                  onChange={(e) =>
                    setNewCategory({ ...newCategory, color: e.target.value })
                  }
                  className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                />
                <button
                  onClick={handleAddCategory}
                  disabled={!newCategory.name.trim()}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </button>
              </div>
            </div>
            {/* Existing Categories */}
            <div className="space-y-2">
              <h4 className="font-medium text-gray-700">Existing Categories</h4>
              <div className="grid gap-2 max-h-60 overflow-y-auto">
                {allCategories.map((category) => (
                  <div
                    key={category.id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                  >
                    {editingCategory === category.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          value={category.name}
                          onChange={(e) =>
                            handleUpdateCategory(category.id, {
                              name: e.target.value,
                            })
                          }
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                          onKeyPress={(e) =>
                            e.key === "Enter" && setEditingCategory(null)
                          }
                          autoFocus
                        />
                        <input
                          type="color"
                          value={category.color}
                          onChange={(e) =>
                            handleUpdateCategory(category.id, {
                              color: e.target.value,
                            })
                          }
                          className="w-8 h-8 rounded border cursor-pointer"
                        />
                        <button
                          onClick={() => setEditingCategory(null)}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: category.color }}
                          />
                          <span className="font-medium">{category.name}</span>
                          <span className="text-sm text-gray-500">
                            ({categoryStats[category.id] || 0})
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setEditingCategory(category.id)}
                            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {!DEFAULT_CATEGORIES.find(
                            (c) => c.id === category.id
                          ) && (
                            <button
                              onClick={() => handleDeleteCategory(category.id)}
                              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={() => setShowAddCategory(false)}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-emerald-600 text-base font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryManagementModal;
